import React, { useState } from 'react';
import { Cookie, Calendar, Sun, Moon, ArrowRightLeft, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { TransferTab } from './components/TransferTab';
import { AfternoonTransferTab } from './components/AfternoonTransferTab';
import { useBreadTransfers } from './hooks/useBreadTransfers';
import { useAfternoonTransfers } from './hooks/useAfternoonTransfers';
import { TransferShift } from './types/bread-transfers.types';

export const BreadTransfersPage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [activeTab, setActiveTab] = useState<TransferShift>('morning');

  const {
    loading,
    error,
    getSession,
    createSession,
    updateTransferRow,
    completeSession,
    resetSession
  } = useBreadTransfers(selectedDate);

  const {
    loading: afternoonTransferLoading,
    error: afternoonTransferError,
    getSession: getAfternoonTransferSession,
    createSession: createAfternoonTransferSession,
    updateTransferRow: updateAfternoonTransferRow,
    completeSession: completeAfternoonTransferSession,
    resetSession: resetAfternoonTransferSession
  } = useAfternoonTransfers(selectedDate);

  const morningSession = getSession('morning');
  const afternoonSession = getSession('afternoon');
  const afternoonTransferSession = getAfternoonTransferSession();

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
  };

  if (error || afternoonTransferError) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-red-700">Error al cargar datos</h2>
          <p className="text-red-600 mt-2">{error || afternoonTransferError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Cookie className="w-6 h-6 text-amber-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Módulo de Transferencias de Pan
            </h1>
            <p className="text-gray-600">
              Gestión de distribución de pan por turnos
            </p>
          </div>
        </div>

        {/* Date Selector */}
        <Card className="w-full lg:w-auto">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <Label htmlFor="date">Fecha:</Label>
              <Input
                id="date"
                type="date"
                value={selectedDate}
                onChange={(e) => handleDateChange(e.target.value)}
                className="w-auto"
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
          </CardContent>
        </Card>
      </div>


      {/* Navigation Tabs */}
      <div className="border-b">
        <nav className="flex space-x-8">
          {[
            { id: 'morning', label: 'Turno Mañana', icon: Sun },
            { id: 'afternoon', label: 'Pedido Tarde', icon: Moon },
            { id: 'afternoon-transfer', label: 'Transferencia Tarde', icon: ArrowRightLeft }
          ].map((tab) => {
            const Icon = tab.icon;
            let session;
            if (tab.id === 'morning') {
              session = morningSession;
            } else if (tab.id === 'afternoon') {
              session = afternoonSession;
            } else {
              session = afternoonTransferSession;
            }
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TransferShift)}
                className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-macartur-primary text-macartur-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {session?.status === 'completed' && (
                  <Badge variant="secondary" className="ml-1 bg-green-100 text-green-800">
                    ✓
                  </Badge>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'morning' && (
          <TransferTab
            shift="morning"
            session={morningSession}
            onCreateSession={createSession}
            onUpdateRow={updateTransferRow}
            onCompleteSession={completeSession}
            onResetSession={resetSession}
            loading={loading}
          />
        )}
        
        {activeTab === 'afternoon' && (
          <TransferTab
            shift="afternoon"
            session={afternoonSession}
            onCreateSession={createSession}
            onUpdateRow={updateTransferRow}
            onCompleteSession={completeSession}
            onResetSession={resetSession}
            loading={loading}
          />
        )}

        {activeTab === 'afternoon-transfer' && (
          <AfternoonTransferTab
            session={afternoonTransferSession}
            onCreateSession={createAfternoonTransferSession}
            onUpdateRow={updateAfternoonTransferRow}
            onCompleteSession={completeAfternoonTransferSession}
            onResetSession={resetAfternoonTransferSession}
            loading={afternoonTransferLoading}
          />
        )}
      </div>
    </div>
  );
};